// Crypto operations for wallet connection and signature verification
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { ethers } from 'ethers';
import { gcm } from '@noble/ciphers/aes.js';
import { fetchFromIPFS as fetchFromIPFSLib, uploadToPinata as uploadToPinataLib } from '@peopletag/protocol';

/**
 * Check if MetaMask is installed
 */
export const isMetaMaskInstalled = () => {
  return typeof window.ethereum !== 'undefined' && window.ethereum.isMetaMask;
};

/**
 * Format wallet address for display (e.g., 0x1234...5678)
 */
export const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * Generate a temporary wallet address from username for users without wallets
 * This creates a deterministic address that can be used for unencrypted messaging
 * Also stores a mapping of address -> username in localStorage for later retrieval
 * @param {string} username - The username
 * @returns {string} A deterministic wallet address (starts with 0x)
 */
export const generateTemporaryWalletFromUsername = (username) => {
  if (!username) {
    throw new Error('Username is required to generate temporary wallet address');
  }
  
  // Create a deterministic hash from the username
  // Use a prefix to distinguish temporary addresses from real ones
  const prefix = 'PeopleTag-Temp-';
  const message = `${prefix}${username}`;
  
  // Hash the message using ethers keccak256
  const hash = ethers.keccak256(ethers.toUtf8Bytes(message));
  
  // Take the first 40 characters of the hash (20 bytes) and prepend 0x
  // This creates a valid Ethereum address format
  const address = '0x' + hash.slice(2, 42);
  
  // Store mapping of address -> username in localStorage for later retrieval
  try {
    const tempAddressMap = JSON.parse(localStorage.getItem('tempAddressToUsername') || '{}');
    tempAddressMap[address.toLowerCase()] = username;
    localStorage.setItem('tempAddressToUsername', JSON.stringify(tempAddressMap));
  } catch (error) {
    console.warn('Error storing temporary address mapping:', error);
  }
  
  return address;
};

/**
 * Get username from a temporary wallet address (if it was generated from a username)
 * @param {string} address - The temporary wallet address
 * @returns {string|null} The username if found, null otherwise
 */
export const getUsernameFromTemporaryAddress = (address) => {
  if (!address) return null;
  
  try {
    const tempAddressMap = JSON.parse(localStorage.getItem('tempAddressToUsername') || '{}');
    return tempAddressMap[address.toLowerCase()] || null;
  } catch (error) {
    console.warn('Error reading temporary address mapping:', error);
    return null;
  }
};

/**
 * Generate a message for the user to sign to prove wallet ownership
 */
const generateSignMessage = (address) => {
  const timestamp = Date.now();
  return `Welcome to PeopleTag!

Please sign this message to prove ownership of your wallet.

Wallet: ${address}
Timestamp: ${timestamp}

This request will not trigger a blockchain transaction or cost any gas fees.`;
};

/**
 * Verify signature using ethers.js
 * Properly verifies that the signature was created by the address for the given message
 */
const verifySignature = async (address, message, signature) => {
  if (!address || !message || !signature) {
    return false;
  }
  
  try {
    // Use ethers to properly verify the signature
    const recoveredAddress = ethers.verifyMessage(message, signature);
    // Compare addresses case-insensitively
    return recoveredAddress.toLowerCase() === address.toLowerCase();
  } catch (error) {
    console.error('Signature verification error:', error);
    return false;
  }
};

/**
 * Connect to MetaMask and verify wallet ownership via signature
 */
export const connectWallet = async () => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask to continue.');
  }

  try {
    // Step 1: Request account access
    const accounts = await window.ethereum.request({
      method: 'eth_requestAccounts',
    });

    if (accounts.length === 0) {
      throw new Error('No accounts found. Please unlock MetaMask.');
    }

    const address = accounts[0];

    // Step 2: Generate message for signature
    const message = generateSignMessage(address);

    // Step 3: Request signature to prove ownership
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [message, address],
    });

    // Step 4: Verify signature (basic check - should be done on backend in production)
    const isValid = await verifySignature(address, message, signature);
    
    if (!isValid) {
      throw new Error('Signature verification failed. Please try again.');
    }

    // Step 5: Store connection data
    const connectionData = {
      address,
      message,
      signature,
      timestamp: Date.now(),
    };

    // Save to localStorage
    localStorage.setItem('walletAddress', address);
    localStorage.setItem('walletSignature', JSON.stringify(connectionData));

    return {
      address,
      signature,
      message,
    };
  } catch (error) {
    console.error('Error connecting wallet:', error);
    
    // Handle specific error codes
    if (error.code === 4001) {
      throw new Error('Connection rejected. Please approve the connection request.');
    } else if (error.code === -32602) {
      throw new Error('Invalid parameters. Please try again.');
    } else if (error.code === -32603) {
      throw new Error('Internal error. Please try again.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('An error occurred while connecting to MetaMask.');
    }
  }
};

/**
 * Disconnect wallet and clear stored data
 */
export const disconnectWallet = () => {
  localStorage.removeItem('walletAddress');
  localStorage.removeItem('walletSignature');
};


/**
 * Get saved wallet address from localStorage
 */
export const getSavedWalletAddress = () => {
  return localStorage.getItem('walletAddress');
};

/**
 * Get saved wallet signature data from localStorage
 */
export const getSavedWalletSignature = () => {
  const signatureData = localStorage.getItem('walletSignature');
  if (signatureData) {
    try {
      return JSON.parse(signatureData);
    } catch (e) {
      return null;
    }
  }
  return null;
};

/**
 * Check if wallet is connected and signature is valid
 */
export const isWalletConnected = () => {
  const address = getSavedWalletAddress();
  const signatureData = getSavedWalletSignature();
  
  if (!address || !signatureData) {
    return false;
  }

  // Check if signature is not too old (e.g., valid for 30 days)
  const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
  if (signatureData.timestamp < thirtyDaysAgo) {
    // Signature expired, need to reconnect
    disconnectWallet();
    return false;
  }

  return true;
};

/**
 * Listen for account changes in MetaMask
 */
export const setupAccountChangeListener = (onAccountChange) => {
  if (!isMetaMaskInstalled() || !window.ethereum) {
    return null;
  }

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      // User disconnected their account
      disconnectWallet();
      onAccountChange(null);
    } else {
      // User switched accounts
      disconnectWallet();
      onAccountChange(accounts[0]);
    }
  };

  window.ethereum.on('accountsChanged', handleAccountsChanged);

  // Return cleanup function
  return () => {
    if (window.ethereum && window.ethereum.removeListener) {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
    }
  };
};

/**
 * Unified authentication function that combines:
 * - Proof of wallet ownership
 * - Encryption key generation
 * 
 * This function requests a single signature that is used for both purposes.
 * @param {string} address - Wallet address
 * @returns {Promise<{encryptionKey: Uint8Array, signature: string, message: string}>}
 */
export const authenticateWalletUnified = async (address) => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed.');
  }

  if (!window.ethereum) {
    throw new Error('MetaMask provider is not available. Please ensure MetaMask is installed and unlocked.');
  }

  if (!address) {
    throw new Error('Wallet address is required.');
  }

  try {
    // Create unified message that covers both purposes
    // IMPORTANT: This message must be FIXED (no timestamp) to ensure deterministic encryption key generation
    // The same wallet address will always generate the same encryption key
    const unifiedMessage = `Welcome to PeopleTag!

Please sign this message to:
1. Prove ownership of your wallet
2. Generate your encryption key for profile data

Wallet: ${address}

This signature will be used for:
- Wallet authentication
- Encryption key generation (derived from this signature)

This does not cost gas and cannot access your funds.

Nonce: PeopleTag-unified-auth-v1`;

    console.log('Requesting signature from MetaMask...');
    console.log('Message to sign:', unifiedMessage);
    
    // Request single signature
    let signature;
    try {
      signature = await window.ethereum.request({
        method: 'personal_sign',
        params: [unifiedMessage, address],
      });
      
      if (!signature) {
        throw new Error('MetaMask returned empty signature. Please try again.');
      }
      
      console.log('Signature received from MetaMask');
    } catch (signError) {
      // Provide clearer error messages for signature request failures
      if (signError.code === 4001) {
        throw new Error('Signature request rejected. Please approve the signature request in MetaMask to continue.');
      } else if (signError.code === -32002) {
        throw new Error('Signature request already pending. Please check MetaMask and approve the signature request.');
      } else if (signError.message) {
        throw new Error(`Failed to get signature: ${signError.message}. Please check MetaMask and try again.`);
      } else {
        throw new Error('Failed to get signature from MetaMask. Please ensure MetaMask is unlocked and try again.');
      }
    }

    // Verify signature (basic check)
    const isValid = await verifySignature(address, unifiedMessage, signature);
    if (!isValid) {
      throw new Error('Signature verification failed. Please try again.');
    }

    // Convert signature (hex string) to bytes (Uint8Array) for encryption key derivation
    const signatureBytes = ethers.getBytes(signature);
    
    // Convert salt and info to Uint8Array for HKDF
    const saltBytes = new TextEncoder().encode('PeopleTag-encryption-v1');
    const infoBytes = new TextEncoder().encode('PeopletagEncryptionKey');

    // Derive 256-bit encryption key using HKDF
    // This key will be the SAME every time for the same wallet address
    // because the message is fixed (no timestamp)
    const encryptionKey = hkdf(
      sha256,
      signatureBytes,                    // Input key material (signature) - Uint8Array
      saltBytes,                         // Salt - Uint8Array
      infoBytes,                         // Info - Uint8Array
      32                                 // 32 bytes = 256 bits for AES-256
    );

    // Store connection data for proof of ownership
    const connectionData = {
      address,
      message: unifiedMessage,
      signature,
      timestamp: Date.now(), // Store timestamp separately for expiration checking
    };

    // Save to localStorage
    localStorage.setItem('walletAddress', address);
    localStorage.setItem('walletSignature', JSON.stringify(connectionData));

    return {
      encryptionKey,      // Uint8Array (32 bytes)
      signature,          // Hex string
      message: unifiedMessage
    };
  } catch (error) {
    console.error('Error in unified authentication:', error);
    
    // If error already has a clear message, re-throw it
    if (error.message && (
      error.message.includes('Signature request rejected') ||
      error.message.includes('Signature request already pending') ||
      error.message.includes('Failed to get signature')
    )) {
      throw error;
    }
    
    // Handle specific error codes
    if (error.code === 4001) {
      throw new Error('Signature request rejected. Please approve the signature request in MetaMask to continue.');
    } else if (error.code === -32602) {
      throw new Error('Invalid parameters. Please try again.');
    } else if (error.code === -32603) {
      throw new Error('Internal error. Please try again.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('An error occurred during authentication. Please try again.');
    }
  }
};

/**
 * Generate encryption key from a pre-existing signature (without requesting new signature)
 * This function derives a 256-bit key from a saved signature
 * @param {string} signature - Pre-existing signature (hex string)
 * @returns {Uint8Array} Encryption key (32 bytes)
 */
export const generateEncryptionKeyFromSignature = (signature) => {
  if (!signature) {
    throw new Error('Signature is required');
  }

  try {
    // Convert signature (hex string) to bytes (Uint8Array)
    const signatureBytes = ethers.getBytes(signature);
    
    // Convert salt and info to Uint8Array for HKDF
    const saltBytes = new TextEncoder().encode('PeopleTag-encryption-v1');
    const infoBytes = new TextEncoder().encode('PeopletagEncryptionKey');

    // Derive 256-bit encryption key using HKDF
    // This key will be the SAME every time for the same signature
    const encryptionKey = hkdf(
      sha256,
      signatureBytes,                    // Input key material (signature) - Uint8Array
      saltBytes,                         // Salt - Uint8Array
      infoBytes,                         // Info - Uint8Array
      32                                 // 32 bytes = 256 bits for AES-256
    );

    return encryptionKey;
  } catch (error) {
    console.error('Error generating encryption key from signature:', error);
    throw new Error('Failed to generate encryption key from signature: ' + error.message);
  }
};

/**
 * Generate encryption key from wallet signature (backward compatibility)
 * This function requests a signature for encryption key generation and derives a 256-bit key
 * @param {string} address - Wallet address
 * @returns {Promise<{encryptionKey: Uint8Array, signature: string, message: string}>}
 */
export const generateEncryptionKey = async (address) => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed.');
  }

  if (!address) {
    throw new Error('Wallet address is required.');
  }

  try {
    // Create unique message for encryption key generation
    // Using fixed message for stable key generation (same signature = same key)
    const encryptionMessage = `Sign to generate your encryption key for PeopleTag.

This signature will ONLY be used to derive a key for encrypting your profile.

This does not cost gas and cannot access your funds.

Nonce: PeopleTag-encryption-key-v1`;

    // Request signature
    // MetaMask's personal_sign accepts the message as a string
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [encryptionMessage, address],
    });

    // Convert signature (hex string) to bytes (Uint8Array)
    const signatureBytes = ethers.getBytes(signature);
    
    // Convert salt and info to Uint8Array as well (HKDF expects Uint8Array for all parameters)
    const saltBytes = new TextEncoder().encode('PeopleTag-encryption-v1');
    const infoBytes = new TextEncoder().encode('PeopletagEncryptionKey');

    // Derive 256-bit encryption key using HKDF
    const encryptionKey = hkdf(
      sha256,
      signatureBytes,                    // Input key material (signature) - Uint8Array
      saltBytes,                         // Salt - Uint8Array
      infoBytes,                         // Info - Uint8Array
      32                                 // 32 bytes = 256 bits for AES-256
    );

    return {
      encryptionKey,      // Uint8Array (32 bytes)
      signature,          // Hex string
      message: encryptionMessage
    };
  } catch (error) {
    console.error('Error generating encryption key:', error);
    
    // Handle specific error codes
    if (error.code === 4001) {
      throw new Error('Signature request rejected. Please approve the signature request to generate your encryption key.');
    } else if (error.code === -32602) {
      throw new Error('Invalid parameters. Please try again.');
    } else if (error.code === -32603) {
      throw new Error('Internal error. Please try again.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('An error occurred while generating encryption key.');
    }
  }
};

/**
 * Convert encryption key (Uint8Array) to hex string for display
 * @param {Uint8Array} key - Encryption key
 * @returns {string} Hex string representation
 */
export const encryptionKeyToHex = (key) => {
  if (!key || !(key instanceof Uint8Array)) {
    return '';
  }
  return '0x' + Array.from(key)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Convert bytes to hex string
 * @param {Uint8Array} bytes - Bytes to convert
 * @returns {string} Hex string representation
 */
const bytesToHex = (bytes) => {
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

/**
 * Encrypt profile data using AES-GCM
 * @param {Object} profile - Profile data to encrypt
 * @param {Uint8Array} key - 32-byte AES-GCM encryption key
 * @returns {Object} Encrypted data with ciphertext and nonce
 */
export const encryptProfile = (profile, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid encryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!profile) {
    throw new Error('Profile data is required.');
  }

  try {
    // Convert profile to JSON string and then to bytes
    const plaintext = new TextEncoder().encode(JSON.stringify(profile));

    // Generate random 96-bit (12-byte) nonce for AES-GCM
    const nonce = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt using AES-GCM
    // In @noble/ciphers v2, gcm takes key and nonce directly
    const cipher = gcm(key, nonce);
    const ciphertext = cipher.encrypt(plaintext);

    return {
      ciphertext: bytesToHex(ciphertext),
      nonce: bytesToHex(nonce)
    };
  } catch (error) {
    console.error('Error encrypting profile:', error);
    throw new Error('Failed to encrypt profile data: ' + error.message);
  }
};

/**
 * Convert hex string to bytes (Uint8Array)
 * @param {string} hex - Hex string
 * @returns {Uint8Array} Bytes
 */
const hexToBytes = (hex) => {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16); // Fixed: use substring instead of deprecated substr
  }
  return bytes;
};

/**
 * Decrypt profile data using AES-GCM
 * @param {Object} encryptedData - Encrypted data with ciphertext and nonce
 * @param {Uint8Array} key - 32-byte AES-GCM decryption key
 * @returns {Object} Decrypted profile data
 */
export const decryptProfile = (encryptedData, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid decryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!encryptedData || !encryptedData.ciphertext || !encryptedData.nonce) {
    throw new Error('Invalid encrypted data. Must contain ciphertext and nonce.');
  }

  try {
    // Convert hex strings to bytes
    const ciphertext = hexToBytes(encryptedData.ciphertext);
    const nonce = hexToBytes(encryptedData.nonce);

    // Decrypt using AES-GCM
    const cipher = gcm(key, nonce);
    const plaintext = cipher.decrypt(ciphertext);

    // Convert bytes to JSON string and parse
    const jsonString = new TextDecoder().decode(plaintext);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decrypting profile:', error);
    throw new Error('Failed to decrypt profile data: ' + error.message);
  }
};

/**
 * In-memory cache for Firestore encryption key (performance optimization)
 * Key is derived from saved signature in localStorage, so no expiration needed
 */
let firestoreEncryptionKeyCache = null;

/**
 * Get or create Firestore encryption key from signature
 * This key is used to encrypt/decrypt Firestore metadata to protect from PeopleTag viewing
 * @param {string} address - Current user's wallet address
 * @param {boolean} forceNew - Force new signature even if cached
 * @returns {Promise<Uint8Array>} 32-byte encryption key
 */
export const getFirestoreEncryptionKey = async (address, forceNew = false) => {
  if (!address) {
    throw new Error('Address is required');
  }

  const normalize = (addr) => {
    if (!addr || typeof addr !== 'string') {
      throw new Error('Invalid address: must be a non-empty string');
    }
    let normalized = addr.toLowerCase().trim();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    if (!/^0x[a-f0-9]{40}$/.test(normalized)) {
      throw new Error(`Invalid Ethereum address format: ${address}`);
    }
    return normalized;
  };

  const normalizedAddress = normalize(address);

  // Check if we have a cached key (performance optimization)
  if (!forceNew && firestoreEncryptionKeyCache) {
    console.log('✅ Using cached Firestore encryption key');
    return firestoreEncryptionKeyCache;
  }

  // Try to derive key from saved signature (like profile does)
  // This allows messages to work on page refresh without re-signing
  if (!forceNew) {
    try {
      const savedSignatureData = getSavedWalletSignature();
      if (savedSignatureData && savedSignatureData.signature) {
        const savedAddress = normalize(getSavedWalletAddress() || '');
        if (savedAddress === normalizedAddress) {
          console.log('✅ Deriving Firestore encryption key from saved signature (no re-signing needed)');
          
          // Derive Firestore encryption key from saved signature
          // Uses different salt/info than profile key to ensure different key
          const signatureBytes = ethers.getBytes(savedSignatureData.signature);
          const saltBytes = new TextEncoder().encode('PeopleTag-firestore-encryption-v1');
          const infoBytes = new TextEncoder().encode('PeopleTagFirestoreEncryptionKey');
          
          const encryptionKey = hkdf(sha256, signatureBytes, saltBytes, infoBytes, 32);
          
          // Cache in memory for performance
          firestoreEncryptionKeyCache = encryptionKey;
          console.log('Firestore encryption key derived from saved signature and cached');
          
          return encryptionKey;
        } else {
          console.log('⚠️ Saved signature address does not match current address, will request new signature');
        }
      } else {
        console.log('⚠️ No saved signature found, will request new signature');
      }
    } catch (error) {
      console.warn('Error deriving Firestore key from saved signature:', error);
      // Fall through to request new signature
    }
  }

  // No saved signature - need to request signature
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask to enable messaging.');
  }

  // Verify this is the current user's address
  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    if (accounts.length === 0) {
      throw new Error('No accounts found. Please connect your wallet.');
    }
    const currentAddress = normalize(accounts[0]);
    if (normalizedAddress !== currentAddress) {
      throw new Error(`Cannot get encryption key for address ${address}. Can only get key for current user's address.`);
    }
  } catch (error) {
    if (error.message.includes('Cannot get encryption key')) {
      throw error;
    }
    throw new Error('Could not verify wallet connection');
  }

  // Request signature for Firestore encryption key
  const firestoreMessage = `Enable secure messaging metadata encryption for PeopleTag

This signature will be used to encrypt/decrypt your messaging metadata in Firestore.

Wallet: ${normalizedAddress}

This protects your message metadata from being viewed by PeopleTag.

This does not cost gas and cannot access your funds.

Nonce: PeopleTag-firestore-encryption-v1`;

  try {
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [firestoreMessage, normalizedAddress],
    });

    // Verify signature
    const isValid = await verifySignature(normalizedAddress, firestoreMessage, signature);
    if (!isValid) {
      throw new Error('Signature verification failed');
    }

    // Derive encryption key from signature
    const signatureBytes = ethers.getBytes(signature);
    const saltBytes = new TextEncoder().encode('PeopleTag-firestore-encryption-v1');
    const infoBytes = new TextEncoder().encode('PeopleTagFirestoreEncryptionKey');
    
    const encryptionKey = hkdf(sha256, signatureBytes, saltBytes, infoBytes, 32);
    
    // Cache in memory for performance
    firestoreEncryptionKeyCache = encryptionKey;
    console.log('Firestore encryption key cached');
    
    return encryptionKey;
  } catch (error) {
    if (error.code === 4001) {
      throw new Error('Signature request rejected. Please approve the signature to enable secure messaging.');
    }
    throw error;
  }
};

/**
 * Check if Firestore encryption key is available (cached or can be derived from saved signature)
 * @param {string} address - Current user's wallet address
 * @returns {boolean} True if key is available, false otherwise
 */
export const hasFirestoreEncryptionKey = (address) => {
  if (!address) return false;
  
  const normalize = (addr) => {
    if (!addr || typeof addr !== 'string') return null;
    let normalized = addr.toLowerCase().trim();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    if (!/^0x[a-f0-9]{40}$/.test(normalized)) {
      return null;
    }
    return normalized;
  };

  const normalizedAddress = normalize(address);
  if (!normalizedAddress) return false;

  // Check if we have a cached key
  if (firestoreEncryptionKeyCache) {
    console.log('Firestore encryption key cache found');
    return true;
  }

  // Check if we can derive from saved signature
  try {
    const savedSignatureData = getSavedWalletSignature();
    if (savedSignatureData && savedSignatureData.signature) {
      const savedAddress = normalize(getSavedWalletAddress() || '');
      if (savedAddress === normalizedAddress) {
        console.log('Firestore encryption key can be derived from saved signature');
        return true;
      }
    }
  } catch (error) {
    console.warn('Error checking saved signature:', error);
  }
  
  console.log('No Firestore encryption key available');
  return false;
};

/**
 * Get remaining time until Firestore encryption key expires
 * Since key is derived from saved signature, it never expires
 * @param {string} address - Current user's wallet address (for validation)
 * @returns {number|null} Always returns a large number if key is available, null otherwise
 */
export const getFirestoreKeyRemainingTime = (address) => {
  if (!address) return null;
  
  // Check if key is available (cached or can be derived)
  if (hasFirestoreEncryptionKey(address)) {
    // Return a large number to indicate key is available (effectively never expires)
    return Number.MAX_SAFE_INTEGER;
  }
  
  return null;
};

/**
 * Clear Firestore encryption key cache (call when user closes messaging)
 * Note: Key can still be derived from saved signature, so this is just for performance
 */
export const clearFirestoreEncryptionKey = () => {
  firestoreEncryptionKeyCache = null;
  console.log('Firestore encryption key cache cleared');
};

/**
 * Encrypt Firestore document data
 * @param {Object} data - Data to encrypt
 * @param {Uint8Array} key - 32-byte encryption key
 * @returns {Object} Encrypted data with ciphertext and nonce
 */
export const encryptFirestoreData = (data, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid encryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!data) {
    throw new Error('Data is required.');
  }

  try {
    // Convert data to JSON string and then to bytes
    const plaintext = new TextEncoder().encode(JSON.stringify(data));

    // Generate random 96-bit (12-byte) nonce for AES-GCM
    const nonce = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt using AES-GCM
    const cipher = gcm(key, nonce);
    const ciphertext = cipher.encrypt(plaintext);

    return {
      _encrypted: true,
      ciphertext: bytesToHex(ciphertext),
      nonce: bytesToHex(nonce)
    };
  } catch (error) {
    console.error('Error encrypting Firestore data:', error);
    throw new Error('Failed to encrypt Firestore data: ' + error.message);
  }
};

/**
 * Decrypt Firestore document data
 * @param {Object} encryptedData - Encrypted data with ciphertext and nonce
 * @param {Uint8Array} key - 32-byte decryption key
 * @returns {Object} Decrypted data
 */
export const decryptFirestoreData = (encryptedData, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid decryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!encryptedData || !encryptedData._encrypted) {
    // Not encrypted, return as-is
    return encryptedData;
  }

  if (!encryptedData.ciphertext || !encryptedData.nonce) {
    throw new Error('Invalid encrypted data. Must contain ciphertext and nonce.');
  }

  try {
    // Convert hex strings to bytes
    const ciphertext = hexToBytes(encryptedData.ciphertext);
    const nonce = hexToBytes(encryptedData.nonce);

    // Decrypt using AES-GCM
    const cipher = gcm(key, nonce);
    const plaintext = cipher.decrypt(ciphertext);

    // Convert bytes to JSON string and parse
    const jsonString = new TextDecoder().decode(plaintext);
    return JSON.parse(jsonString);
  } catch (error) {
    // "invalid ghash tag" errors are expected when:
    // - Data was encrypted with a different key
    // - User re-authenticated and key changed
    // - Data is corrupted
    // Only log non-authentication errors as they indicate actual problems
    const isAuthError = error.message && error.message.includes('invalid ghash tag');
    if (!isAuthError) {
      console.error('Error decrypting Firestore data:', error);
    }
    throw new Error('Failed to decrypt Firestore data: ' + error.message);
  }
};

/**
 * Fetch encrypted data from IPFS using multiple gateways with fallback
 * Tries multiple IPFS gateways to handle CORS issues
 * @param {string} ipfsHash - IPFS hash (CID)
 * @param {Object} options - Optional configuration
 * @returns {Promise<Object>} Encrypted data object with ciphertext and nonce
 */
export const fetchFromIPFS = async (ipfsHash, options = {}) => {
  if (!ipfsHash) {
    throw new Error('IPFS hash (CID) is required');
  }

  // List of IPFS gateways to try (in order of preference)
  const gateways = [
    'https://gateway.pinata.cloud/ipfs',
    'https://ipfs.io/ipfs',
    'https://cloudflare-ipfs.com/ipfs',
    'https://dweb.link/ipfs',
    'https://gateway.ipfs.io/ipfs'
  ];

  // If a custom gateway is provided, try it first
  if (options.gateway) {
    gateways.unshift(options.gateway);
  }

  let lastError = null;

  // Try each gateway until one succeeds (with per-gateway timeout of 3s)
  for (const gateway of gateways) {
    try {
      const gatewayUrl = `${gateway}/${ipfsHash}`;
      console.log(`📥 Trying to fetch from IPFS gateway: ${gateway}`);
      
      // Add timeout per gateway (3s) to prevent long waits
      const gatewayTimeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Gateway timeout')), 3000);
      });
      
      const response = await Promise.race([
        fetch(gatewayUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          // Add mode to handle CORS
          mode: 'cors',
          cache: 'no-cache'
        }),
        gatewayTimeout
      ]);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const encryptedData = await response.json();
      console.log(`✅ Successfully fetched data from IPFS using ${gateway}`);
      
      return encryptedData;
    } catch (error) {
      if (error.message === 'Gateway timeout') {
        console.warn(`⏱️ Gateway ${gateway} timed out, trying next...`);
      } else {
        console.warn(`❌ Failed to fetch from ${gateway}:`, error.message);
      }
      lastError = error;
      // Continue to next gateway
      continue;
    }
  }

  // All gateways failed
  console.error('❌ All IPFS gateways failed. Last error:', lastError);
  throw new Error(`Failed to fetch data from IPFS after trying ${gateways.length} gateways: ${lastError?.message || 'Unknown error'}`);
};

/**
 * Upload encrypted data to Pinata IPFS
 * Wrapper that uses environment variables for credentials with fallback
 * @param {Object} encryptedData - Encrypted data object with ciphertext and nonce
 * @param {string} filename - Optional filename (default: 'profile.json')
 * @returns {Promise<string>} IPFS hash (CID) of the uploaded file
 */
export const uploadToPinata = async (encryptedData, filename = 'profile.json') => {
  // Get from environment variables with fallback (like the working version)
  const PINATA_API_KEY = process.env.REACT_APP_PINATA_API_KEY || '9cc1f9390227b905d56b';
  const PINATA_SECRET_API_KEY = process.env.REACT_APP_PINATA_SECRET_API_KEY || '396eb3bfcaf0b7ee52e4933b876820f865a9a53e8e03486debb3057617afaaf5';

  // Debug: Check if environment variables are loaded
  console.log('📤 Uploading to Pinata...');
  console.log('  REACT_APP_PINATA_API_KEY from env:', !!process.env.REACT_APP_PINATA_API_KEY);
  console.log('  REACT_APP_PINATA_SECRET_API_KEY from env:', !!process.env.REACT_APP_PINATA_SECRET_API_KEY);
  console.log('  Using API Key:', PINATA_API_KEY ? PINATA_API_KEY.substring(0, 10) + '...' : 'NOT FOUND');
  console.log('  API Key length:', PINATA_API_KEY ? PINATA_API_KEY.length : 0);
  console.log('  Secret Key length:', PINATA_SECRET_API_KEY ? PINATA_SECRET_API_KEY.length : 0);

  try {
    const result = await uploadToPinataLib(encryptedData, {
      apiKey: PINATA_API_KEY,
      secretKey: PINATA_SECRET_API_KEY,
      filename: filename
    });
    console.log('✅ Pinata upload successful, CID:', result);
    return result;
  } catch (error) {
    console.error('❌ Pinata upload error details:');
    console.error('  Error type:', typeof error);
    console.error('  Error message:', error?.message);
    console.error('  Error string:', String(error));
    console.error('  Full error object:', error);
    
    // Try to extract the actual error from the nested error message
    let errorMessage = 'Unknown error';
    if (error?.message) {
      errorMessage = error.message;
      // If the message contains [object Object], try to get more details
      if (errorMessage.includes('[object Object]')) {
        // Check if there's a cause or original error
        if (error.cause) {
          errorMessage = error.cause.message || String(error.cause);
        } else if (error.originalError) {
          errorMessage = error.originalError.message || String(error.originalError);
        } else {
          errorMessage = 'Pinata API returned 403 Forbidden - API keys are invalid or expired. Please check your Pinata API credentials.';
        }
      }
    } else if (typeof error === 'string') {
      errorMessage = error;
    } else {
      errorMessage = String(error);
    }
    
    // If we still have [object Object], provide a helpful message
    if (errorMessage.includes('[object Object]')) {
      errorMessage = 'Pinata API returned 403 Forbidden - API keys are invalid or expired. The hardcoded fallback keys may no longer be valid. Please set REACT_APP_PINATA_API_KEY and REACT_APP_PINATA_SECRET_API_KEY environment variables with valid Pinata API keys.';
    }
    
    throw new Error(`Failed to upload to Pinata: ${errorMessage}`);
  }
};

/**
 * Request a signature for messaging encryption
 * This signature is used to derive a shared encryption key with another party
 * @param {string} address - Wallet address to sign with
 * @param {string} otherPartyAddress - The other party's address (for context in message)
 * @returns {Promise<string>} Signature hex string
 */
export const requestMessagingSignature = async (address, otherPartyAddress) => {
  console.log('requestMessagingSignature called with:', { address, otherPartyAddress });
  
  if (!isMetaMaskInstalled()) {
    console.error('MetaMask is not installed');
    throw new Error('MetaMask is not installed.');
  }

  if (!address) {
    console.error('Wallet address is required');
    throw new Error('Wallet address is required.');
  }

  if (!window.ethereum) {
    console.error('window.ethereum is not available');
    throw new Error('Ethereum provider is not available. Please ensure MetaMask is installed and unlocked.');
  }

  // Create a fixed message for messaging encryption
  // This message is deterministic - same addresses = same message = same signature
  const normalizeAddress = (addr) => {
    let normalized = addr.toLowerCase();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    return normalized;
  };

  const addr1 = normalizeAddress(address);
  const addr2 = normalizeAddress(otherPartyAddress);
  
  // Sort addresses to ensure deterministic message (same regardless of order)
  const sortedAddresses = [addr1, addr2].sort();
  
  const messagingMessage = `PeopleTag Messaging Encryption

This signature is used to generate a shared encryption key for secure messaging.

Your address: ${addr1}
Other party: ${addr2}

This does not cost gas and cannot access your funds.

Nonce: PeopleTag-messaging-v1-${sortedAddresses[0]}-${sortedAddresses[1]}`;

  console.log('requestMessagingSignature: Requesting signature from MetaMask');
  console.log('Message to sign:', messagingMessage);
  console.log('Address:', address);
  
  try {
    // Request signature
    console.log('Calling window.ethereum.request for personal_sign...');
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [messagingMessage, address],
    });

    console.log('Signature received from MetaMask:', signature ? 'Yes' : 'No');
    if (!signature) {
      throw new Error('MetaMask returned empty signature');
    }

    return signature;
  } catch (error) {
    console.error('Error requesting messaging signature:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    
    if (error.code === 4001) {
      throw new Error('Signature request rejected. Please approve to enable secure messaging.');
    } else if (error.code === -32602) {
      throw new Error('Invalid parameters. Please try again.');
    } else if (error.code === -32603) {
      throw new Error('Internal error. Please try again.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('An error occurred while requesting signature.');
    }
  }
};

/**
 * Generate a deterministic ephemeral keypair from a wallet address
 * This allows both parties to generate the same keypair independently
 * @param {string} address - Wallet address
 * @returns {ethers.Wallet} Deterministic wallet instance
 */
const generateDeterministicKeypair = (address) => {
  // Normalize address
  const normalize = (addr) => {
    let normalized = addr.toLowerCase();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    return normalized;
  };
  
  const normalizedAddress = normalize(address);
  
  // Create a deterministic seed from the address
  // Use HKDF to derive a private key from the address
  const addressBytes = new TextEncoder().encode(normalizedAddress);
  const saltBytes = new TextEncoder().encode('PeopleTag-ECDH-seed-v1');
  const infoBytes = new TextEncoder().encode('PeopleTagDeterministicKeypair');
  
  // Derive 32-byte seed for wallet creation
  const seed = hkdf(sha256, addressBytes, saltBytes, infoBytes, 32);
  
  // Convert seed to hex string for ethers wallet
  const seedHex = Array.from(seed)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  
  // Create wallet from deterministic seed
  // Note: This creates a wallet with a deterministic private key derived from the address
  // Both parties can independently generate the same wallet for the same address
  return new ethers.Wallet('0x' + seedHex);
};

/**
 * Get or create a messaging keypair from signature (cached)
 * SECURE: Uses unique private keys derived from signatures, not addresses
 * Keys are cached in localStorage to avoid repeated signature requests
 * NOTE: Can only get keypair for the current user's address (requires signature)
 * @param {string} address - Wallet address (must be current user's address)
 * @param {string} currentUserAddress - Current user's wallet address (for validation)
 * @returns {Promise<ethers.Wallet>} Wallet instance with unique keypair
 */
export const getMessagingKeypair = async (address, currentUserAddress = null) => {
  if (!address) {
    throw new Error('Address is required');
  }

  const normalize = (addr) => {
    if (!addr || typeof addr !== 'string') {
      throw new Error('Invalid address: must be a non-empty string');
    }
    let normalized = addr.toLowerCase().trim();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    // Validate Ethereum address format (0x followed by 40 hex characters)
    if (!/^0x[a-f0-9]{40}$/.test(normalized)) {
      throw new Error(`Invalid Ethereum address format: ${address}. Must be 0x followed by 40 hexadecimal characters.`);
    }
    return normalized;
  };

  const normalizedAddress = normalize(address);
  
  // If currentUserAddress is provided, verify this is the current user's address
  // We can only request signatures for the current user's address
  if (currentUserAddress) {
    const normalizedCurrent = normalize(currentUserAddress);
    if (normalizedAddress !== normalizedCurrent) {
      throw new Error(`Cannot get keypair for address ${address}. Can only get keypair for current user's address (${currentUserAddress}). For other addresses, their public key must be retrieved from Firestore.`);
    }
  }
  
  const cacheKey = `messagingKeypair_${normalizedAddress}`;

  // Check if we have a cached keypair in sessionStorage (changed from localStorage for security)
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const keypairData = JSON.parse(cached);
      // Verify it's for the correct address
      if (keypairData.address === normalizedAddress) {
        console.log('✅ Using cached messaging keypair from sessionStorage for', normalizedAddress);
        // Recreate wallet from private key
        return new ethers.Wallet(keypairData.privateKey);
      } else {
        console.warn('⚠️ Cached keypair address mismatch:', keypairData.address, 'vs', normalizedAddress);
      }
    } else {
      console.log('❌ No cached messaging keypair found in sessionStorage for', normalizedAddress);
    }
  } catch (error) {
    console.warn('Error reading cached keypair:', error);
  }

  // No cached keypair - need to request signature
  // But first, verify this is the current user's address
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed. Please install MetaMask to enable messaging.');
  }

  // Get current user's address from MetaMask to verify
  try {
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    if (accounts.length === 0) {
      throw new Error('No accounts found. Please connect your wallet.');
    }
    const currentAddress = normalize(accounts[0]);
    if (normalizedAddress !== currentAddress) {
      throw new Error(`Cannot request signature for address ${address}. Can only request signatures for your own address (${accounts[0]}).`);
    }
  } catch (error) {
    if (error.message.includes('Cannot request signature')) {
      throw error;
    }
    // If we can't verify, still try to proceed (might be in a different context)
    console.warn('Could not verify address matches current user:', error);
  }

  // Request signature for messaging keypair
  const messagingMessage = `Enable secure messaging for PeopleTag

This signature will be used to generate your unique messaging keypair.

Wallet: ${normalizedAddress}

This does not cost gas and cannot access your funds.

Nonce: PeopleTag-messaging-keypair-v1`;

  try {
    const signature = await window.ethereum.request({
      method: 'personal_sign',
      params: [messagingMessage, normalizedAddress],
    });

    // Verify signature
    const isValid = await verifySignature(normalizedAddress, messagingMessage, signature);
    if (!isValid) {
      throw new Error('Signature verification failed');
    }

    // Derive unique keypair from signature (NOT from address)
    const signatureBytes = ethers.getBytes(signature);
    const saltBytes = new TextEncoder().encode('PeopleTag-messaging-keypair-v1');
    const infoBytes = new TextEncoder().encode('PeopleTagMessagingKeypair');
    
    // Derive 32-byte seed from signature
    const seed = hkdf(sha256, signatureBytes, saltBytes, infoBytes, 32);
    
    // Convert seed to hex string for ethers wallet
    const seedHex = Array.from(seed)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    // Create wallet from seed derived from signature
    const wallet = new ethers.Wallet('0x' + seedHex);
    
    // Cache the keypair in sessionStorage (cleared on tab close) instead of localStorage
    // This reduces XSS attack window - private keys are only stored for the current session
    // SECURITY: Private keys should ideally not be stored at all, but sessionStorage is safer than localStorage
    const keypairData = {
      address: normalizedAddress,
      privateKey: wallet.privateKey,
      timestamp: Date.now()
    };
    
    try {
      // Use sessionStorage instead of localStorage for better security
      // sessionStorage is cleared when tab closes, reducing attack surface
      sessionStorage.setItem(cacheKey, JSON.stringify(keypairData));
      console.log('✅ Messaging keypair cached in sessionStorage for', normalizedAddress);
    } catch (error) {
      console.warn('Failed to cache keypair:', error);
      // Continue anyway - keypair is still valid, just not cached
    }
    
    return wallet;
  } catch (error) {
    if (error.code === 4001) {
      throw new Error('Signature request rejected. Please approve the signature to enable messaging.');
    }
    throw error;
  }
};

/**
 * Generate a shared encryption key for messaging between two wallet addresses
 * SECURE VERSION: Uses ECDH (Elliptic Curve Diffie-Hellman) key exchange
 * Uses unique keypairs derived from signatures (not addresses)
 * Requires signatures from both parties (cached after first use)
 * 
 * @param {string} address1 - First wallet address
 * @param {string} address2 - Second wallet address
 * @returns {Promise<Uint8Array>} Shared encryption key (32 bytes)
 */
// Cache for shared message keys (address1_address2 -> key)
const sharedKeyCache = new Map();

export const generateSharedMessageKey = async (address1, address2) => {
  if (!address1 || !address2) {
    throw new Error('Both addresses are required for key generation');
  }

  // Normalize addresses
  const normalize = (addr) => {
    let normalized = addr.toLowerCase();
    if (!normalized.startsWith('0x')) {
      normalized = '0x' + normalized;
    }
    return normalized;
  };
  
  const addr1 = normalize(address1);
  const addr2 = normalize(address2);
  
  // Sort addresses to ensure deterministic key (same key regardless of order)
  const sorted = [addr1, addr2].sort();
  const [firstAddr, secondAddr] = sorted;
  
  // Create cache key
  const cacheKey = `sharedKey_${firstAddr}_${secondAddr}`;
  
  // Check cache first (shared keys are deterministic, so safe to cache)
  if (sharedKeyCache.has(cacheKey)) {
    return sharedKeyCache.get(cacheKey);
  }
  
  // CRITICAL FIX: Use deterministic keypairs for BOTH parties to ensure shared keys match
  // The previous implementation used signature-based keypairs for the current user and
  // deterministic keypairs for the other party, which caused mismatched shared keys.
  // 
  // For ECDH to work, both parties must use the SAME keypair generation method:
  // - If sender uses signature-based + receiver uses deterministic → keys don't match ✗
  // - If both use deterministic → keys match ✓
  //
  // We use deterministic keypairs for both parties so:
  // - Sender: deterministicPrivateKey_sender * deterministicPublicKey_receiver
  // - Receiver: deterministicPrivateKey_receiver * deterministicPublicKey_sender
  // Since ECDH is commutative, these produce the same shared secret ✓
  //
  // TODO: In future, implement proper ECDH by storing public keys in Firestore
  // so both parties can use signature-based keypairs for better security
  const wallet1 = generateDeterministicKeypair(firstAddr);
  const wallet2 = generateDeterministicKeypair(secondAddr);
  
  // Perform ECDH key exchange
  // Both parties compute: sharedSecret = privateKey1 * publicKey2 = privateKey2 * publicKey1
  // We use the signing key's computeSharedSecret method
  const sharedSecret = wallet1.signingKey.computeSharedSecret(wallet2.signingKey.publicKey);
  
  // Convert shared secret (hex string) to bytes
  const sharedSecretBytes = ethers.getBytes(sharedSecret);
  
  // Use HKDF to derive a 256-bit encryption key from the shared secret
  const saltBytes = new TextEncoder().encode('PeopleTag-message-encryption-v4-ECDH');
  const infoBytes = new TextEncoder().encode('PeopleTagSharedMessageKeyECDH');
  
  // Derive 256-bit shared encryption key using HKDF
  // This key can ONLY be derived by parties who have their unique private keys
  // Since keypairs are derived from signatures (not addresses), only the actual parties can decrypt
  const sharedKey = hkdf(
    sha256,
    sharedSecretBytes,          // Input key material (ECDH shared secret)
    saltBytes,                  // Salt
    infoBytes,                  // Info
    32                          // 32 bytes = 256 bits for AES-256
  );

  // Cache the key for future use
  sharedKeyCache.set(cacheKey, sharedKey);
  
  return sharedKey;
};

/**
 * Encrypt a message using a shared encryption key
 * @param {string} message - Message text to encrypt
 * @param {Uint8Array} key - 32-byte AES-GCM encryption key
 * @returns {Object} Encrypted data with ciphertext and nonce
 */
export const encryptMessage = (message, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid encryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!message || typeof message !== 'string') {
    throw new Error('Message is required and must be a string.');
  }

  try {
    // Convert message to bytes
    const plaintext = new TextEncoder().encode(message);

    // Generate random 96-bit (12-byte) nonce for AES-GCM
    const nonce = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt using AES-GCM
    const cipher = gcm(key, nonce);
    const ciphertext = cipher.encrypt(plaintext);

    return {
      ciphertext: bytesToHex(ciphertext),
      nonce: bytesToHex(nonce)
    };
  } catch (error) {
    console.error('Error encrypting message:', error);
    throw new Error('Failed to encrypt message: ' + error.message);
  }
};

/**
 * Decrypt a message using a shared encryption key
 * @param {Object} encryptedData - Encrypted data with ciphertext and nonce
 * @param {Uint8Array} key - 32-byte AES-GCM decryption key
 * @returns {string} Decrypted message text
 */
export const decryptMessage = (encryptedData, key) => {
  if (!key || !(key instanceof Uint8Array) || key.length !== 32) {
    throw new Error('Invalid decryption key. Key must be a 32-byte Uint8Array.');
  }

  if (!encryptedData || !encryptedData.ciphertext || !encryptedData.nonce) {
    throw new Error('Invalid encrypted data. Must contain ciphertext and nonce.');
  }

  try {
    // Convert hex strings to bytes
    const ciphertext = hexToBytes(encryptedData.ciphertext);
    const nonce = hexToBytes(encryptedData.nonce);

    // Decrypt using AES-GCM
    const cipher = gcm(key, nonce);
    const plaintext = cipher.decrypt(ciphertext);

    // Convert bytes to string
    return new TextDecoder().decode(plaintext);
  } catch (error) {
    console.error('Error decrypting message:', error);
    throw new Error('Failed to decrypt message: ' + error.message);
  }
};

